import {
  useAvailableToolTypes,
  usePartnerTools,
  useUpdateTool,
  useCreateOrUpdateTool,
} from '@app/api/tool_settings.api';
import { ToolSettingModel } from '@app/domain/ToolSettingModel';
import { Status } from '@app/components/profile/profileCard/profileFormNav/nav/payments/paymentHistory/Status/Status';
import { Priority } from '@app/constants/enums/priorities';
import { defineColorByPriority } from '@app/utils/utils';
import { Form, Input, message, Modal, Space, Switch, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Pagination } from 'api/table.api';
import { Table } from 'components/common/Table/Table';
import { Button } from 'components/common/buttons/Button/Button';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 100,
};

interface Props {
  partnerID: string;
}

const formatToolType = (toolType: string): string => {
  return toolType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Extended type for display that includes tool types without settings
type DisplayToolModel = ToolSettingModel & {
  tool_type: string;
  is_enabled: boolean;
  configuration: Record<string, any> | null;
  effective_configuration: Record<string, any>;
  can_be_disabled: boolean;
  id?: string; // Optional for tools without settings
  partner_id?: string;
  created?: string;
  updated?: string;
};

export const ToolsTable: React.FC<Props> = ({ partnerID }) => {
  const { t } = useTranslation();
  const { data: tools, isLoading } = usePartnerTools(partnerID);
  const { data: availableToolTypes } = useAvailableToolTypes();
  const updateToolMutation = useUpdateTool(partnerID);
  const createOrUpdateToolMutation = useCreateOrUpdateTool(partnerID);

  const [tableData, setTableData] = useState<{ data: DisplayToolModel[]; pagination: Pagination; loading: boolean }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });

  const [toolOrder, setToolOrder] = useState<string[]>([]); // Store original tool order by tool_type

  const [configModalOpen, setConfigModalOpen] = useState<{ [key: string]: boolean }>({});
  const [configForm] = Form.useForm();
  const [editingTool, setEditingTool] = useState<DisplayToolModel | null>(null);

  // Merge available tool types with existing tool settings
  useEffect(() => {
    if (!availableToolTypes?.available_tools) return;

    const toolsMap = new Map<string, ToolSettingModel>();
    if (tools) {
      tools.forEach((tool) => {
        toolsMap.set(tool.tool_type, tool);
      });
    }

    // Create display data: merge available tool types with existing settings
    const displayData: DisplayToolModel[] = availableToolTypes.available_tools.map((toolType) => {
      const existingSetting = toolsMap.get(toolType.tool_type);
      if (existingSetting) {
        return existingSetting;
      } else {
        // Tool type exists but no setting yet - create display model
        return {
          tool_type: toolType.tool_type,
          is_enabled: false,
          configuration: null,
          effective_configuration: toolType.default_configuration || {},
          can_be_disabled: true,
        } as DisplayToolModel;
      }
    });

    // Save order by tool_type on first load
    if (toolOrder.length === 0) {
      setToolOrder(displayData.map((t) => t.tool_type));
    }

    // Sort according to saved order
    const sortedData = toolOrder
      .map((toolType) => displayData.find((t) => t.tool_type === toolType))
      .filter((t): t is DisplayToolModel => t !== undefined)
      .concat(displayData.filter((t) => !toolOrder.includes(t.tool_type))); // Add any new tools at the end

    setTableData({
      data: sortedData,
      pagination: initialPagination,
      loading: isLoading,
    });
  }, [tools, availableToolTypes, isLoading, toolOrder]);

  const handleToggleTool = async (tool: DisplayToolModel, checked: boolean) => {
    // All tools can now be toggled, including opensearch
    try {
      // Optimistically update the UI
      setTableData((prev) => ({
        ...prev,
        data: prev.data.map((item) => (item.tool_type === tool.tool_type ? { ...item, is_enabled: checked } : item)),
      }));

      // If tool has an id, it exists - update it. Otherwise, create it.
      if (tool.id) {
        await updateToolMutation.mutateAsync({
          toolType: tool.tool_type,
          updateData: { is_enabled: checked },
        });
      } else {
        // Create new tool setting
        await createOrUpdateToolMutation.mutateAsync({
          tool_type: tool.tool_type,
          is_enabled: checked,
          configuration: tool.configuration,
        });
      }
      message.success(t(checked ? 'common.toolEnabled' : 'common.toolDisabled'));
    } catch (error) {
      // Revert on error
      setTableData((prev) => ({
        ...prev,
        data: prev.data.map((item) => (item.tool_type === tool.tool_type ? { ...item, is_enabled: !checked } : item)),
      }));
      message.error(t('common.errorUpdatingTool'));
      console.error(error);
    }
  };

  const handleOpenConfigModal = (tool: DisplayToolModel) => {
    setEditingTool(tool);
    const configToShow = tool.configuration || tool.effective_configuration || {};
    configForm.setFieldsValue({
      configuration: JSON.stringify(configToShow, null, 2),
    });
    const modalKey = tool.id || tool.tool_type;
    setConfigModalOpen({ ...configModalOpen, [modalKey]: true });
  };

  const handleSaveConfig = async () => {
    if (!editingTool) return;

    try {
      const configValue = configForm.getFieldValue('configuration');
      let parsedConfig: Record<string, any> | null = null;

      if (configValue && configValue.trim()) {
        try {
          parsedConfig = JSON.parse(configValue);
        } catch (e) {
          message.error(t('common.invalidJsonConfig'));
          return;
        }
      }

      // If tool has an id, it exists - update it. Otherwise, create it.
      if (editingTool.id) {
        await updateToolMutation.mutateAsync({
          toolType: editingTool.tool_type,
          updateData: { configuration: parsedConfig },
        });
      } else {
        await createOrUpdateToolMutation.mutateAsync({
          tool_type: editingTool.tool_type,
          is_enabled: editingTool.is_enabled,
          configuration: parsedConfig,
        });
      }

      message.success(t('common.configUpdated'));
      const modalKey = editingTool.id || editingTool.tool_type;
      setConfigModalOpen({ ...configModalOpen, [modalKey]: false });
      setEditingTool(null);
      configForm.resetFields();
    } catch (error) {
      message.error(t('common.errorUpdatingConfig'));
      console.error(error);
    }
  };

  const getToolDescription = (toolType: string): string => {
    const toolInfo = availableToolTypes?.available_tools.find((t) => t.tool_type === toolType);
    return toolInfo?.description || '';
  };

  const columns: ColumnsType<DisplayToolModel> = [
    {
      title: t('common.toolType'),
      dataIndex: 'tool_type',
      render: (toolType: string) => (
        <div>
          <div style={{ fontWeight: 500 }}>{formatToolType(toolType)}</div>
          <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>{getToolDescription(toolType)}</div>
        </div>
      ),
    },
    {
      title: t('common.status'),
      dataIndex: 'is_enabled',
      width: '10%',
      render: (isEnabled: boolean) => (
        <Status
          color={defineColorByPriority(isEnabled ? Priority.LOW : Priority.INFO)}
          text={isEnabled ? t('common.enabled') : t('common.disabled')}
        />
      ),
    },
    {
      title: t('common.alwaysEnabled'),
      dataIndex: 'can_be_disabled',
      width: '12%',
      render: (canBeDisabled: boolean) => (
        <Tag color={canBeDisabled ? 'default' : 'red'}>{canBeDisabled ? t('common.no') : t('common.yes')}</Tag>
      ),
    },
    {
      title: t('common.configuration'),
      dataIndex: 'effective_configuration',
      width: '20%',
      render: (config: Record<string, any>) => (
        <div style={{ fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {config ? JSON.stringify(config).substring(0, 50) + '...' : t('common.noConfig')}
        </div>
      ),
    },
    {
      title: t('tables.actions'),
      dataIndex: 'id',
      width: '25%',
      render: (id, record: DisplayToolModel) => {
        const switchKey = `${record.tool_type}-switch`;
        return (
          <Space>
            <Switch
              key={switchKey}
              checked={record.is_enabled}
              onChange={(checked) => handleToggleTool(record, checked)}
            />
            <Button type="default" size="small" onClick={() => handleOpenConfigModal(record)}>
              {t('common.config')}
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={tableData.data.map((data) => ({ ...data, key: data.tool_type }))}
        pagination={tableData.pagination}
        loading={tableData.loading}
        scroll={{ x: 1000 }}
        bordered
        rowKey="tool_type"
      />
      <Modal
        title={`${t('common.configTool')}: ${editingTool ? formatToolType(editingTool.tool_type) : ''}`}
        open={editingTool ? configModalOpen[editingTool.id || editingTool.tool_type] || false : false}
        onOk={handleSaveConfig}
        onCancel={() => {
          if (editingTool) {
            const modalKey = editingTool.id || editingTool.tool_type;
            setConfigModalOpen({ ...configModalOpen, [modalKey]: false });
            setEditingTool(null);
            configForm.resetFields();
          }
        }}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        width={600}
        confirmLoading={updateToolMutation.isLoading || createOrUpdateToolMutation.isLoading}
      >
        <Form form={configForm} layout="vertical">
          <Form.Item name="configuration" label={t('common.configurationJson')} tooltip={t('common.configJsonTooltip')}>
            <Input.TextArea
              rows={10}
              placeholder={t('common.configJsonPlaceholder')}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
          {/* {editingTool && editingTool.effective_configuration && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontWeight: 500, marginBottom: '8px' }}>Cấu hình hiện tại:</div>
              <pre style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px', fontSize: '12px' }}>
                {JSON.stringify(editingTool.effective_configuration, null, 2)}
              </pre>
            </div>
          )} */}
        </Form>
      </Modal>
    </>
  );
};
